import { apiService, type ApiResponse } from './api';
import type { ApiNominationResponse } from '../types';
import type { ReviewDetailsResponse } from './nominationReviewService';

export interface PublicFeedbackNomination {
  id: string;
  employeeId: string;
  employeeName: string;
  designation: string;
  department: string;
  nominationAs: string;
  reviewExists: boolean;
  relationshipId: string;
}

export interface PublicFeedbackDetails {
  employee: {
    employeeId: string;
    employeeName: string;
    employeeEmail?: string;
    designation: string;
    department: string;
    nominationAs: string;
    reportingManager?: string;
    reportingManagerEmail?: string;
  };
  criteria: Array<{
    id: string;
    description: string;
    tooltip?: string;
    rating?: number;
  }>;
  appreciativeFeedback: string;
  constructiveFeedback: string;
  submitted: boolean;
}

export interface PublicFeedbackPayload {
  nomination_relationship_id: number;
  uuid: string;
  competency: Record<string, number>;
  feedback: {
    appreciative: string;
    constructive: string;
  };
  is_email_required: string;
}

const PUBLIC_FEEDBACK_LIST_ENDPOINT = '/api/v1/feedback/third_party_nomination_review_list/';
const PUBLIC_FEEDBACK_REVIEW_ENDPOINT = '/api/v1/feedback/third_party_nomination_review/';

const COMPETENCY_BY_CRITERION: Record<string, string> = {
  '1': 'solution_focused',
  '2': 'adapt_to_win',
  '3': 'courage_to_act',
  '4': 'act_for_enterprise',
  '5': 'own_it',
};

class PublicFeedbackService {
  async getNominations(uuid: string): Promise<ApiResponse<ApiNominationResponse>> {
    const params = new URLSearchParams({ uuid });
    return apiService.externalGet<ApiNominationResponse>(`${PUBLIC_FEEDBACK_LIST_ENDPOINT}?${params}`);
  }

  async getReview(uuid: string, relationshipId: string): Promise<ApiResponse<ReviewDetailsResponse>> {
    const params = new URLSearchParams({
      uuid,
      nomination_relationship_id: relationshipId,
    });
    return apiService.externalGet<ReviewDetailsResponse>(`${PUBLIC_FEEDBACK_REVIEW_ENDPOINT}?${params}`);
  }

  private buildPayload(
    uuid: string,
    relationshipId: string,
    appreciative: string,
    constructive: string,
    ratings: Record<string, number>,
    isEmailRequired: string,
  ): PublicFeedbackPayload {
    const competency: Record<string, number> = {};
    Object.entries(ratings).forEach(([criterionId, rating]) => {
      const competencyKey = COMPETENCY_BY_CRITERION[criterionId];
      if (competencyKey) competency[competencyKey] = rating;
    });

    return {
      nomination_relationship_id: Number.parseInt(relationshipId, 10),
      uuid,
      competency,
      feedback: { appreciative, constructive },
      is_email_required: isEmailRequired,
    };
  }

  async saveReview(
    uuid: string,
    relationshipId: string,
    appreciative: string,
    constructive: string,
    ratings: Record<string, number>,
  ): Promise<ApiResponse<Record<string, unknown>>> {
    const payload = this.buildPayload(uuid, relationshipId, appreciative, constructive, ratings, 'false');

    try {
      const existingReview = await this.getReview(uuid, relationshipId);
      if (existingReview.status && existingReview.data) {
        return apiService.externalPut<Record<string, unknown>>(PUBLIC_FEEDBACK_REVIEW_ENDPOINT, payload);
      }
    } catch {
      // Follow the protected flow: create the draft if the lookup finds no review.
    }

    return apiService.externalPost<Record<string, unknown>>(PUBLIC_FEEDBACK_REVIEW_ENDPOINT, payload);
  }

  async submitReview(
    uuid: string,
    relationshipId: string,
    appreciative: string,
    constructive: string,
    ratings: Record<string, number>,
  ): Promise<ApiResponse<Record<string, unknown>>> {
    const payload = this.buildPayload(uuid, relationshipId, appreciative, constructive, ratings, 'false');
    return apiService.externalPost<Record<string, unknown>>(PUBLIC_FEEDBACK_REVIEW_ENDPOINT, payload);
  }
}

export default new PublicFeedbackService();